import { cancelClickTimerCallback, leafClaimCache } from './state.js';

// Cache for which roles we've already fetched claim status for
const leafClaimStatusFetched: Set<string> = new Set();

// Flag to prevent concurrent bulk loads
let isLoadingClaimedRoles = false;
let hasLoadedClaimedRoles = false;

// Bulk load all claimed role IDs for current user
async function loadAllClaimedRoles(): Promise<void> {
    // Flag check removed - caller handles this now
    isLoadingClaimedRoles = true;
    hasLoadedClaimedRoles = true;
    try {
        const response = await fetch('/api/user/claimed-role-ids', {
            credentials: 'include'
        });
        if (!response.ok) {
            console.warn('Failed to load claimed roles');
            return;
        }
        const data = await response.json();
        const claimedRoleIds = data.claimedRoleIds || [];
        
        // Update cache with claimed roles
        claimedRoleIds.forEach(roleId => {
            leafClaimCache[String(roleId)] = true;
            leafClaimStatusFetched.add(String(roleId));
        });
        
        console.log(`Loaded ${claimedRoleIds.length} claimed roles`);
    } catch (error) {
        console.error('Error loading claimed roles:', error);
    } finally {
        isLoadingClaimedRoles = false;
    }
}

export async function addCircleLabels(): Promise<void> {
    const chartElement = document.getElementById('chart');
    const svg = chartElement.querySelector('svg');
    if (!svg) return;

    // Load all claimed roles once on first call
    if (!hasLoadedClaimedRoles && !isLoadingClaimedRoles) {
        await loadAllClaimedRoles();
    }

    const mainGroup = svg.querySelector('g');
    if (!mainGroup) return;

    let defs = svg.querySelector('defs');
    if (!defs) {
        defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        svg.insertBefore(defs, svg.firstChild);
    }

    let zoomScale = 1;
    const transform = mainGroup.getAttribute('transform');
    if (transform) {
        const scaleMatch = transform.match(/scale\(([\d.]+)/);
        if (scaleMatch) {
            zoomScale = parseFloat(scaleMatch[1]);
        }
    }

    if (!defs.querySelector('#whiteGlow')) {
        const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
        filter.setAttribute('id', 'whiteGlow');
        filter.setAttribute('x', '-50%');
        filter.setAttribute('y', '-50%');
        filter.setAttribute('width', '200%');
        filter.setAttribute('height', '200%');

        const feFlood = document.createElementNS('http://www.w3.org/2000/svg', 'feFlood');
        feFlood.classList.add('label-glow-flood');
        feFlood.setAttribute('result', 'flood');

        const feComposite = document.createElementNS('http://www.w3.org/2000/svg', 'feComposite');
        feComposite.setAttribute('in', 'flood');
        feComposite.setAttribute('in2', 'SourceAlpha');
        feComposite.setAttribute('operator', 'in');
        feComposite.setAttribute('result', 'mask');

        const feMorphology = document.createElementNS('http://www.w3.org/2000/svg', 'feMorphology');
        feMorphology.setAttribute('in', 'mask');
        feMorphology.setAttribute('radius', '0.3');
        feMorphology.setAttribute('operator', 'dilate');
        feMorphology.setAttribute('result', 'dilated');

        const feGaussianBlur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
        feGaussianBlur.setAttribute('in', 'dilated');
        feGaussianBlur.setAttribute('stdDeviation', '0.3');
        feGaussianBlur.setAttribute('result', 'blurred');

        const feMerge = document.createElementNS('http://www.w3.org/2000/svg', 'feMerge');
        const feMergeNode1 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
        feMergeNode1.setAttribute('in', 'blurred');
        const feMergeNode2 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
        feMergeNode2.setAttribute('in', 'SourceGraphic');

        feMerge.appendChild(feMergeNode1);
        feMerge.appendChild(feMergeNode2);

        filter.appendChild(feFlood);
        filter.appendChild(feComposite);
        filter.appendChild(feMorphology);
        filter.appendChild(feGaussianBlur);
        filter.appendChild(feMerge);

        defs.appendChild(filter);
    }

    mainGroup.querySelectorAll('text.circle-label').forEach(el => el.remove());
    defs.querySelectorAll('path[id^="circlePath-"]').forEach(el => el.remove());
    mainGroup.querySelectorAll('.leaf-label').forEach(el => el.remove());
    mainGroup.querySelectorAll('.label__claim-star, .leaf-claim-star').forEach(el => el.remove());

    const circles = mainGroup.querySelectorAll('circle');

    // First pass: calculate the minimum font size needed per depth level
    // accounting for text overflow scaling
    const fontSizesByDepth: { [key: number]: number } = {};
    circles.forEach((circle) => {
        const circleData = circle.__data__ as any;
        if (!circleData || !circleData.data) return;
        if (!circleData.children || circleData.children.length === 0) {
            const depth = circleData.depth || 0;
            const r = circleData.r || parseFloat(circle.getAttribute('r')) || 0;
            const name = circleData.data.name;
            
            if (name && name.trim() && r > 0) {
                const baseFontSize = Math.max(1, (r * 0.8) / zoomScale);
                
                // Create temporary element to measure text
                const tempLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                tempLabel.classList.add('leaf-label');
                tempLabel.setAttribute('text-anchor', 'middle');
                tempLabel.setAttribute('dominant-baseline', 'middle');
                tempLabel.textContent = name;
                tempLabel.style.setProperty('--leaf-font-size', `${baseFontSize}px`);
                mainGroup.appendChild(tempLabel);
                
                const bbox = (tempLabel as any).getBBox?.();
                tempLabel.remove();
                
                let finalFontSize = baseFontSize;
                if (bbox) {
                    const textWidth = bbox.width;
                    const maxLabelWidth = r * 1.6;
                    if (textWidth > maxLabelWidth) {
                        const scaleFactor = maxLabelWidth / textWidth;
                        finalFontSize = baseFontSize * scaleFactor;
                    }
                }
                
                // Store the most scaled down size for this depth
                if (!fontSizesByDepth[depth] || finalFontSize < fontSizesByDepth[depth]) {
                    fontSizesByDepth[depth] = Math.max(1, finalFontSize);
                }
            }
        }
    });

    circles.forEach((circle, index) => {
        const circleData = circle.__data__ as any;
        if (!circleData || !circleData.data) return;

        const name = circleData.data.name;
        if (!name || !name.trim()) return;
        const r = circleData.r || parseFloat(circle.getAttribute('r')) || 0;
        const cx = circleData.x || parseFloat(circle.getAttribute('cx')) || 0;
        const cy = circleData.y || parseFloat(circle.getAttribute('cy')) || 0;

        const visualRadius = r * zoomScale;
        if (visualRadius < 30) return;
        
        const circleGroup = circle.parentElement;
        
        // For leaf nodes (no children), create custom centered labels
        if (!circleData.children || circleData.children.length === 0) {
            if (circleGroup) {
                // Hide ALL library-created text in this group (including any original stars/labels)
                const allTexts = circleGroup.querySelectorAll('text');
                allTexts.forEach(text => {
                    if (!text.classList.contains('leaf-label') && 
                        !text.classList.contains('label__claim-star') &&
                        !text.classList.contains('circle-label')) {
                        text.classList.add('circle-label-hidden');
                        text.style.display = 'none';  // Extra safety: force hide
                    }
                });
                
                // Also explicitly remove any text elements that contain star characters from circleGroup
                const allCircleGroupTexts = circleGroup.querySelectorAll('text');
                allCircleGroupTexts.forEach(text => {
                    if (text.textContent && (text.textContent.includes('★') || text.textContent.includes('☆'))) {
                        if (!text.classList.contains('label__claim-star')) {
                            text.remove();
                        }
                    }
                });
                
                // Font sizes - same for all leafs at same depth, based on most constrained leaf
                const depth = circleData.depth || 0;
                const labelFontSize = fontSizesByDepth[depth] || 10 / zoomScale;
                const starFontSize = labelFontSize; // Star same size as label
                
                // Create custom centered label (recreated on every zoom like curved labels)
                const customLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                customLabel.classList.add('leaf-label');
                customLabel.setAttribute('x', String(cx));
                customLabel.setAttribute('y', String(cy));
                customLabel.setAttribute('text-anchor', 'middle');
                customLabel.setAttribute('dominant-baseline', 'middle');
                customLabel.setAttribute('font-weight', 'bold');
                customLabel.setAttribute('filter', 'url(#whiteGlow)');
                customLabel.textContent = name;
                
                // Set CSS custom property for font size
                customLabel.style.setProperty('--leaf-font-size', `${labelFontSize}px`);
                mainGroup.appendChild(customLabel);
                
                // Create star (visual indicator only for claimed roles)
                const roleId = circleData.data.id || '';
                const cachedIsClaimed = leafClaimCache[roleId];
                
                // Only create star if role is claimed
                if (cachedIsClaimed) {
                    const starText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                    starText.classList.add('label__claim-star');
                    starText.classList.add('leaf-claim-star');
                    starText.classList.add('claimed');
                    starText.setAttribute('x', String(cx));
                    starText.setAttribute('y', String(cy + labelFontSize * 2));
                    starText.setAttribute('text-anchor', 'middle');
                    starText.setAttribute('dominant-baseline', 'middle');
                    starText.setAttribute('data-role-id', String(roleId));
                    starText.setAttribute('data-role-name', name);
                    starText.textContent = '★';
                    
                    // Set CSS custom property for star size
                    starText.style.setProperty('--star-size', `${starFontSize}px`);
                    
                    mainGroup.appendChild(starText);
                }
                
                // Add double-click handler to circle for claiming/unclaiming (only once)
                if (!circle.hasAttribute('data-claim-handler')) {
                    circle.setAttribute('data-claim-handler', 'true');
                    circle.addEventListener('dblclick', async (e) => {
                        e.stopPropagation();
                        // Cancel pending single-click to avoid double panel refresh
                        if (cancelClickTimerCallback) {
                            cancelClickTimerCallback();
                        }
                        const roleId = circleData.data.id || '';
                        const isClaimed = leafClaimCache[roleId];
                        if (typeof (window as any).toggleLeafClaim === 'function') {
                            const loadRoleUsersCallback = (window as any).loadRoleUsers;
                            await (window as any).toggleLeafClaim(roleId, name, !isClaimed, undefined, loadRoleUsersCallback);
                        }
                    });
                }
            }
            return;
        }

        const maxChars = Math.floor(r / 4);
        const displayName = name; // name.length > maxChars ? name.substring(0, maxChars - 1) + '…' : name;

        const pathId = `circlePath-${index}`;
        const labelRadius = r - 10;

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        const pathData = `M ${cx - labelRadius}, ${cy}
                         a ${labelRadius},${labelRadius} 0 1,1 ${labelRadius * 2},0
                         a ${labelRadius},${labelRadius} 0 1,1 ${-labelRadius * 2},0`;

        path.setAttribute('id', pathId);
        path.setAttribute('d', pathData);
        path.setAttribute('fill', 'none');
        defs.appendChild(path);

        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.classList.add('circle-label');
        // Divide font size by zoom to maintain constant visual size
        const baseFontSize = Math.min(r / 10, 8) / zoomScale;
        text.setAttribute('font-size', String(Math.max(3, baseFontSize)));
        text.setAttribute('filter', 'url(#whiteGlow)');

        const textPath = document.createElementNS('http://www.w3.org/2000/svg', 'textPath');
        textPath.classList.add('circle-label-path');
        textPath.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `#${pathId}`);
        textPath.setAttribute('startOffset', '25%');
        textPath.setAttribute('text-anchor', 'middle');
        textPath.textContent = displayName;

        text.appendChild(textPath);
        mainGroup.appendChild(text);

        // Hide the original centered label for parent nodes
        if (circleGroup) {
            const originalLabel = circleGroup.querySelector('text:not(.circle-label)');
            if (originalLabel) {
                originalLabel.classList.add('circle-label-hidden');
            }
        }
    });
}

// Reset function for tests
export function resetClaimLoadState(): void {
    hasLoadedClaimedRoles = false;
    isLoadingClaimedRoles = false;
    leafClaimStatusFetched.clear();
}

// Expose cache globally for tests
(window as any).leafClaimStatusFetched = leafClaimStatusFetched;
